/* ---------------------------------------------------------------------------
   Favourites, kept on the shopper's device

   The list is an array of product handles in localStorage, newest first. No
   account, no app, nothing on the server - which also means it lives in one
   browser only. That trade-off was chosen on purpose (see the Favourites group
   in theme settings).

   What this file does
   -------------------
   - Toggles [data-favourite-toggle] buttons (snippets/favourite-button.liquid)
     with one delegated click listener, so buttons inside the quick view or in
     cards loaded later work without re-binding.
   - Keeps every button for the same handle in sync, plus the header count
     ([data-favourites-count]).
   - Fills the favourites page ([data-favourites-grid], sections/
     main-favourites.liquid) by fetching each product rendered through
     templates/product.card.liquid, so the cards are the real theme cards with
     the real quick view / add-to-cart behaviour.

   New buttons (quick view, filtered grids) arrive through innerHTML, so a
   MutationObserver re-applies the pressed state when they appear.
   --------------------------------------------------------------------------- */
(function () {
  'use strict';

  var STORAGE_KEY = 'ather-favourites';
  var MAX_ITEMS = 100;

  function read() {
    try {
      var list = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      return Array.isArray(list) ? list.filter(function (h) { return typeof h === 'string' && h; }) : [];
    } catch (e) {
      return [];
    }
  }

  function write(list) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(list.slice(0, MAX_ITEMS)));
    } catch (e) {
      /* Private mode / storage full: the toggle still flips for this page view. */
    }
  }

  var favourites = read();

  function has(handle) {
    return favourites.indexOf(handle) !== -1;
  }

  function toggle(handle) {
    if (has(handle)) {
      favourites = favourites.filter(function (h) { return h !== handle; });
    } else {
      favourites = [handle].concat(favourites);
    }
    write(favourites);
    syncAll();
    document.dispatchEvent(new CustomEvent('favourites:change', {
      detail: { handle: handle, added: has(handle), favourites: favourites.slice() }
    }));
  }

  function syncButton(button) {
    var handle = button.getAttribute('data-handle');
    var on = has(handle);
    if (button.getAttribute('aria-pressed') === String(on)) return;

    var label = button.getAttribute(on ? 'data-label-remove' : 'data-label-add') || '';
    var title = button.getAttribute('data-title') || '';
    button.setAttribute('aria-pressed', String(on));
    button.setAttribute('aria-label', title ? label + ': ' + title : label);
    button.setAttribute('title', label);
  }

  function syncCount() {
    var text = favourites.length ? String(favourites.length) : '';
    document.querySelectorAll('[data-favourites-count]').forEach(function (el) {
      // Only write when it changes: setting textContent replaces the text node,
      // which is a childList mutation - the observer below would see it, call
      // syncAll, write again, and spin forever.
      if (el.textContent !== text) el.textContent = text;
      if (el.hidden !== !text) el.hidden = !text;
    });
  }

  function syncAll(root) {
    (root || document).querySelectorAll('[data-favourite-toggle]').forEach(syncButton);
    syncCount();
  }

  document.addEventListener('click', function (event) {
    var button = event.target.closest('[data-favourite-toggle]');
    if (!button) return;
    // Cards wrap the photo in a link; the button sits on top of it.
    event.preventDefault();
    event.stopPropagation();

    var handle = button.getAttribute('data-handle');
    if (!handle) return;
    toggle(handle);

    button.classList.remove('is-popping');
    void button.offsetWidth; // restart the animation on repeat clicks
    if (has(handle)) button.classList.add('is-popping');
  });

  // Another tab changed the list.
  window.addEventListener('storage', function (event) {
    if (event.key !== STORAGE_KEY) return;
    favourites = read();
    syncAll();
  });

  // Only react to new favourite buttons (quick view, re-rendered grids), never
  // to text changes, so nothing this file writes can retrigger it.
  new MutationObserver(function (mutations) {
    for (var i = 0; i < mutations.length; i++) {
      var added = mutations[i].addedNodes;
      for (var j = 0; j < added.length; j++) {
        var node = added[j];
        if (node.nodeType === 1 && (node.matches('[data-favourite-toggle]') || node.querySelector('[data-favourite-toggle]'))) {
          syncAll();
          return;
        }
      }
    }
  }).observe(document.documentElement, { childList: true, subtree: true });

  /* ---------- favourites page ---------- */

  function initPage(grid) {
    var section = grid.closest('[data-favourites-page]');
    var empty = section && section.querySelector('[data-favourites-empty]');
    var loading = section && section.querySelector('[data-favourites-loading]');

    function showEmpty(isEmpty) {
      if (empty) empty.hidden = !isEmpty;
      grid.hidden = isEmpty;
    }

    function cardUrl(handle) {
      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      return root + 'products/' + encodeURIComponent(handle) + '?view=card';
    }

    var handles = favourites.slice();
    if (!handles.length) {
      if (loading) loading.hidden = true;
      showEmpty(true);
      return;
    }

    Promise.all(handles.map(function (handle) {
      return fetch(cardUrl(handle), { credentials: 'same-origin' })
        .then(function (res) { return res.ok ? res.text() : ''; })
        .catch(function () { return ''; })
        .then(function (html) { return { handle: handle, html: html.trim() }; });
    })).then(function (results) {
      var gone = [];
      results.forEach(function (r) {
        // A deleted or unpublished product 404s; drop it from the list quietly.
        if (!r.html) { gone.push(r.handle); return; }
        var item = document.createElement('div');
        item.className = 'favourites-grid__item';
        item.setAttribute('data-favourite-item', r.handle);
        item.innerHTML = r.html;
        grid.appendChild(item);
      });

      if (gone.length) {
        favourites = favourites.filter(function (h) { return gone.indexOf(h) === -1; });
        write(favourites);
      }

      if (loading) loading.hidden = true;
      syncAll(grid);
      showEmpty(!grid.children.length);
    });

    // Un-favouriting on this page removes the card.
    document.addEventListener('favourites:change', function (event) {
      if (event.detail.added) return;
      var item = grid.querySelector('[data-favourite-item="' + CSS.escape(event.detail.handle) + '"]');
      if (!item) return;
      item.classList.add('is-leaving');
      setTimeout(function () {
        item.remove();
        showEmpty(!grid.children.length);
      }, 250);
    });
  }

  function start() {
    syncAll();
    var grid = document.querySelector('[data-favourites-grid]');
    if (grid) initPage(grid);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
